import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Button } from "antd";
import { WarningCircle, ArrowRight } from "@phosphor-icons/react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { getDeliveryMethodsForAdmin, getPaymentMethods } from "../../../service/client";
import { useBranchContext } from "../../../contexts/branch-context";
import { colors } from "../../../styles/theme";

const Banner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: ${colors.warningLight};
  border-bottom: 1px solid ${colors.warning};
  padding: 10px 24px;
  min-height: 44px;

  @media (max-width: 768px) {
    padding: 10px 16px;
    flex-wrap: wrap;
    gap: 8px;
  }
`;

const BannerLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
`;

const BannerText = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${colors.warningDark};
  line-height: 1.4;
`;

const GoButton = styled(Button)`
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 600;
  border-color: ${colors.warning};
  color: ${colors.warningDark};
  background: transparent;
  height: 30px;
  padding: 0 12px;

  &:hover {
    background: ${colors.warning} !important;
    border-color: ${colors.warning} !important;
    color: #fff !important;
  }
`;

export const OnlineSalesWarningBanner: React.FC = () => {
  const { t } = useTranslation();
  const { currentBranch } = useBranchContext();
  const navigate = useNavigate();

  const isOpen = currentBranch?.isOpen === true;

  const { data: deliveryMethods } = useQuery(
    ["/delivery-methods-banner", currentBranch?.id],
    () => getDeliveryMethodsForAdmin({ branchId: currentBranch!.id }),
    {
      enabled: !!currentBranch?.id && isOpen,
      staleTime: 30_000,
    }
  );

  const { data: paymentMethodsData } = useQuery(
    ["/payment-methods-banner", currentBranch?.id],
    () => getPaymentMethods({ branchId: currentBranch!.id }),
    {
      enabled: !!currentBranch?.id && isOpen,
      staleTime: 30_000,
    }
  );

  const paymentMethods = Array.isArray(paymentMethodsData)
    ? paymentMethodsData
    : paymentMethodsData?.data ?? [];

  const hasNoActiveDelivery =
    Array.isArray(deliveryMethods) &&
    deliveryMethods.filter((m: any) => m.isActive).length === 0;

  const hasNoActivePayments =
    Array.isArray(paymentMethods) &&
    paymentMethods.filter((m: any) => m.isActive).length === 0;

  if (!isOpen) return null;

  return (
    <>
      {hasNoActiveDelivery && (
        <Banner>
          <BannerLeft>
            <WarningCircle size={18} weight="fill" color={colors.warning} style={{ flexShrink: 0 }} />
            <BannerText>
              {t("onlineSalesWarningBanner.noActiveDeliveryText")}
            </BannerText>
          </BannerLeft>
          <GoButton
            icon={<ArrowRight size={14} />}
            iconPosition="end"
            onClick={() => navigate("/settings?tab=online-sales&sub=delivery")}
          >
            {t("onlineSalesWarningBanner.setDeliveryMethodButton")}
          </GoButton>
        </Banner>
      )}
      {hasNoActivePayments && (
        <Banner>
          <BannerLeft>
            <WarningCircle size={18} weight="fill" color={colors.warning} style={{ flexShrink: 0 }} />
            <BannerText>
              {t("onlineSalesWarningBanner.noActivePaymentsText")}
            </BannerText>
          </BannerLeft>
          <GoButton
            icon={<ArrowRight size={14} />}
            iconPosition="end"
            onClick={() => navigate("/settings?tab=online-sales&sub=payments")}
          >
            {t("onlineSalesWarningBanner.setPaymentMethodButton")}
          </GoButton>
        </Banner>
      )}
    </>
  );
};
